import _ from 'underscore';
import { deserializeImageAssetMetadata } from '@bingads-webui-campaign/media-metadata-model';
import { updatePlacementWithBaseCropSettings, updatePlacementWithPlacementSettings } from '@bingads-webui-campaign/image-experience-utils';

/* Restore image model to UI state
 * When there is effect applied, need to convert Metadata to effectSettings
 */
export const processImagePlacementsMetadata =
  (placements, baseImageIdStr, baseImageInfo, isImageFittingEnabled, isCombinedMsanAndMmaAspectRatios) => {
    let imageLevelSettings;
    let propagateToImageLevel = false;

    const includedPlacements = _.filter(placements, placement => placement.IsIncluded);
    const excludedPlacements = _.filter(placements, placement => !placement.IsIncluded);
    const hasBeenBulkCropped = placements[0].baseImage.originalUrl &&
      placements[0].baseImage.originalUrl !== placements[0].baseImage.src;
    const hasPlacementSettings =
      placements[0].baseImage.placementSettings &&
      placements[0].baseImage.placementSettings.length > 0;

    const uniqueCountOfIds = _.chain(includedPlacements)
      .pluck('Id').uniq().size()
      .value();

    if (uniqueCountOfIds === 1) {
      // when placements have same effect, propagate it to image level
      propagateToImageLevel = true;
    }

    const cachedDeserializedMetadataById = {};

    _.each(includedPlacements, (placement) => {
      const cachedValue = cachedDeserializedMetadataById[placement.Id];
      let effectSettings;

      if (_.isUndefined(cachedValue)) {
        const deserializeMetadata = deserializeImageAssetMetadata(placement.Metadata) || {};

        cachedDeserializedMetadataById[placement.Id] = deserializeMetadata;
        ({ effectSettings } = deserializeMetadata);
      } else {
        ({ effectSettings } = cachedValue);
      }

      if (placement.IdString !== baseImageIdStr && effectSettings) {
        // restore placement various settings (effect, crop) in UI state
        const effectSrc = placement.Src;

        placement.setEffectSettings(_.defaults({ effectSrc }, effectSettings));
      }

      if (propagateToImageLevel && !imageLevelSettings) {
        imageLevelSettings = { effectSettings: placement.effectSettings };
      }
    });

    // restore image level UI state when all placements have same effect
    _.each(includedPlacements, (placement) => {
      const { url: originalSrc } = baseImageInfo;

      placement.BaseImage.setSrc(originalSrc);
      placement.BaseImage
        .setEffectSettings(imageLevelSettings && imageLevelSettings.effectSettings);
    });

    // let excluded placements inherit image level
    if (!_.isEmpty(includedPlacements)) {
      _.each(excludedPlacements, (placement) => {
        placement.setBaseImage(includedPlacements[0].BaseImage.clone());
        placement.setEffectSettings(placement.BaseImage.EffectSettings);
      });
    }

    if (hasBeenBulkCropped) {
      const { BaseImage } = placements[0];

      _.each(placements, (placement) => {
        updatePlacementWithBaseCropSettings({
          placement,
          mtImgObject: BaseImage,
          IsCroppingbasedOriginal: true,
          setCroppingType: isImageFittingEnabled,
        });
      });
    }

    if (hasPlacementSettings && !isCombinedMsanAndMmaAspectRatios) {
      const { BaseImage } = placements[0];

      _.each(placements, (placement) => {
        updatePlacementWithPlacementSettings({
          placement,
          mtImgObject: BaseImage,
          updatePlacementSettings:
            !placement.isMaunalCropped(), // fill in placement settings when
          // crop settings are auto-filled in MSAN workflow
          isMultimediaAd: false,
          isMultimediaAdinMSANEnabled: true,
          setCroppingType: isImageFittingEnabled,
        });
      });
    }
  };

const DefaultKey = 'default';

const containMetadata = metadata => !_.isEmpty(metadata);
export const getMetadataKey = metadata => (containMetadata(metadata) ? metadata : DefaultKey);

/* This is to optimize bulkUpsert call to create new assets.
  If asset content exists in db, same asset id is returned from MT.
  So here we collect such information to reduce unnecessary calls.

Return result is a dictionary whose key is asset id which doesn't have any effects applied.
{
  [base-id-1]: {
    default: { id, src },
    [metadata1]: { id, src },
  },
}

*/
export const collectExistingMetadata = assetList => _.reduce(assetList, (memo, asset) => {
  const key = asset.BaseId.toString();
  const original = memo[key];
  const metadataKey = getMetadataKey(asset.Metadata);
  const isProcessed = containMetadata(asset.Metadata);

  return {
    ...memo,
    [key]: _.extend({
      ...original,
      [metadataKey]: {
        // id and src should be matched
        // when apply effects, we only modify EffectSettings and keep src read-only.
        id: asset.Id,
        bingSrc: asset.Src,
      },
    }, isProcessed && {
      [DefaultKey]: {
        id: asset.BaseImage.Id,
        bingSrc: asset.BaseImage.Src,
      },
    }),
  };
}, {});

export const insertMetadata = (dict, id, metadata, data) => {
  const key = getMetadataKey(metadata);
  _.extend(dict[id.toString()], {
    [key]: {
      id: data.id,
      bingSrc: data.bingSrc,
    },
  });
};

export const findMetadata = (dict, id, metadata) => {
  const metadataKey = getMetadataKey(metadata);
  const assetsFromImage = dict[id.toString()];

  return assetsFromImage[metadataKey];
};

export const isMetadataExisting = (dict, id, metadata) => {
  const found = findMetadata(dict, id, metadata);

  return !_.isUndefined(found);
};
